import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useSelector} from 'react-redux';
import {
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ID_SIREN,
    CONTROL_TYPE_ID_THERMOSTAT,
    INPUT_TYPE_ID_SCENARIO,
    INPUT_TYPE_ID_SENSOR,
    InputModel,
    InputTypeModel, SensorTypeModel,
} from '../../../../types/control';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useSetSelector} from '../../../../hooks/set-selector';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {useMap} from '../../../../hooks/map';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useMapSelector} from '../../../../hooks/map-selector';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings/input';

type CONTROL_VALUE = {
    id: string;
    isVirtual: boolean;
};

export const InputEditScreen = memo(function InputEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation<{ controlId: string; bridgeDeviceId: string; inputTypeId: string; input: InputModel; }>();
    const previousLocationSelectedControl = useSelector(state => state.control.controlList.find(c => c.id === location?.state?.controlId));
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();

    const inputList = useSelector(state => state.control.inputList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.input_count || 0) > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(bridgeDevice => ({
                value: bridgeDevice,
                label: bridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state?.input || inputList.find(control => control.id === params?.id) || null, []);
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? (location?.state?.bridgeDeviceId == null ? null :
                bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === location?.state?.bridgeDeviceId) || null) :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id)
            || null),
    );
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);
    const controlSet = useSetSelector(state => state.control.controlList);
    const inputTypeList = useSelector(state => state.control.inputTypeList);
    const inputTypeOptionList: SelectItem<InputTypeModel>[] =
        useMemo(() => inputTypeList
            .map(inputType => ({
                value: inputType,
                label: inputType.name,
            })), [inputTypeList]);
    const [inputType, setInputType] = useState<SelectItem<InputTypeModel>>(
        (selected == null ? (location?.state?.inputTypeId == null ? null :
            inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === location.state.inputTypeId)
            || null) : inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === selected.type_id)
            || null),
    );
    // 0 setAlarm input type
    const sensorTypeOptionList = useMap(
        useFilterSelector(state => state.control.sensorTypeList, sensor => sensor.id !== '0', []),
        state => ({value: state, label: state.name}),
        []);
    const [sensorTypeOption, setSensorTypeOption] = useState<SelectItem<SensorTypeModel>>(
        (selected == null || selected.type_id !== INPUT_TYPE_ID_SENSOR ? null : (
                sensorTypeOptionList.find(sensorTypeOption => sensorTypeOption.value.id === selected.sensor_settings?.sensor_type_id)
                || null)
        ),
    );
    const isInputTypeSensor = useMemo(() => inputType?.value?.id === INPUT_TYPE_ID_SENSOR || false, [inputType]);
    const bridgeDeviceFilteredControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.controlList,
            control => control.bridge_device_id === bridgeDevice?.value.id &&
                control.type_id !== CONTROL_TYPE_ID_THERMOSTAT && (
                    isInputTypeSensor ?
                        control.type_id === CONTROL_TYPE_ID_ALARM :
                        (control.type_id !== CONTROL_TYPE_ID_ALARM && control.type_id !== CONTROL_TYPE_ID_SIREN)
                ),
            [isInputTypeSensor, bridgeDevice?.value],
        ),
        controlModel => ({label: controlModel.name, value: {isVirtual: false, id: controlModel.id}}),
        []);
    const bridgeDeviceFilteredVirtualControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.virtualControlList,
            virtualControl => virtualControl.bridge_device_id === bridgeDevice?.value.id && (
                isInputTypeSensor ?
                    controlSet[virtualControl.control_id]?.type_id === CONTROL_TYPE_ID_ALARM :
                    (controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_ALARM &&
                        controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_SIREN)
            ),
            [isInputTypeSensor, bridgeDevice?.value],
        ),
        virtualControl => ({
            label: controlSet[virtualControl.control_id]?.name || '',
            value: {isVirtual: true, id: virtualControl.id},
        }),
        [controlSet]);
    const controlOptionList: SelectItem<CONTROL_VALUE>[] = useMemo(() => [
        ...bridgeDeviceFilteredControlOptionList,
        ...bridgeDeviceFilteredVirtualControlOptionList,
    ], [bridgeDeviceFilteredControlOptionList, bridgeDeviceFilteredVirtualControlOptionList]);
    const inputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const inputCount = bridgeDeviceType?.input_count || 0;
        if (inputCount === 0) {
            return [];
        }

        const list = [];
        for (let i = 0; i < inputCount; i++) {
            list.push({value: i, label: t('entrance') + ' ' + i.toString()});
        }

        return list;
    }, [t, bridgeDeviceType]);

    const [name, setName] = useState((selected?.id != null && selected?.name) || '');
    const [controlOption, setControlOption] = useState<SelectItem<CONTROL_VALUE>>(
        selected == null ? null : (
            selected?.is_virtual_control ?
                bridgeDeviceFilteredVirtualControlOptionList.find(option => option.value.id === selected?.relational_id) :
                bridgeDeviceFilteredControlOptionList.find(option => option.value.id === selected?.relational_id)
        ) || null,
    );
    const [inputNumber, setInputNumber] = useState<SelectItem<number>>(
        (selected == null ? null : inputNumberOptionList.find(option => option.value === selected.input_number)
            || null),
    );
    const DIRECTION_OPTION_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('close_stop'),
        },
        {
            value: 1,
            label: t('open_up'),
        },
        {
            value: 2,
            label: t('down'),
        },
        {
            value: 3,
            label: t('open_close'),
        },
    ], [t]);
    const [direction, setDirection] = useState<SelectItem<number>>(
        (selected == null ? DIRECTION_OPTION_LIST[0] : DIRECTION_OPTION_LIST.find(option => option.value === selected.direction)
            || null),
    );
    const [outputReverse, setOutputReverse] = useState(selected?.should_output_reverse || false);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_input'), () => {
            return rpc.deleteInput(selected.id)
                .then(() => {
                    success(t('input_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [
        t,
        failure,
        onBackPressed,
        question,
        rpc,
        selected?.id,
        success,
    ]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_name'));

            return false;
        }

        if (bridgeDevice?.value == null) {
            failure(t('pls_select_device'));

            return false;
        }

        if (inputType?.value == null) {
            failure(t('pls_select_a_input_type'));

            return false;
        }

        if (inputNumber?.value == null) {
            failure(t('pls_select_a_input'));

            return false;
        }

        if (inputType.value.id !== INPUT_TYPE_ID_SCENARIO) {
            if (direction?.value == null) {
                failure(t('pls_select_a_direction'));

                return false;
            }

            if (controlOption?.value == null) {
                failure(t('pls_select_control'));

                return false;
            }

            if (inputType.value.id === INPUT_TYPE_ID_SENSOR) {
                if (sensorTypeOption?.value == null) {
                    failure(t('pls_select_a_sensor_type'));

                    return false;
                }
            }
        }

        return true;
    }, [
        t,
        failure,
        name,
        bridgeDevice,
        inputType,
        inputNumber,
        direction,
        controlOption,
        sensorTypeOption,
    ]);

    const onGoBackPressed = useCallback(() => {
        if (previousLocationSelectedControl?.id != null) {
            success(t('control_successfully_saved'));
        }

        onBackPressed();
    }, [t, previousLocationSelectedControl?.id, success, onBackPressed]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const input: InputModel = {
            id: selected?.id || null,
            is_active: true,
            is_notification: false,
            name,
            bridge_device_id: bridgeDevice.value.id,
            type_id: inputType.value.id,
            direction: (inputType.value.id === INPUT_TYPE_ID_SCENARIO || inputType.value.id === INPUT_TYPE_ID_SENSOR) ? 1 : direction.value,
            is_virtual_control: controlOption?.value.isVirtual || false,
            relational_id: inputType.value.id === INPUT_TYPE_ID_SCENARIO ? null : controlOption?.value.id,
            should_output_reverse: outputReverse,
            input_number: inputNumber.value,
            sensor_settings: inputType.value.id !== INPUT_TYPE_ID_SENSOR ? null : {
                sensor_type_id: sensorTypeOption?.value?.id,
            },
        };

        return rpc.updateInput(input)
            .then(() => {
                const message = selected?.id != null ?
                    t('input_successfully_edited') :
                    previousLocationSelectedControl?.id == null ?
                        t('input_successfully_saved') :
                        t('control_and_input_successfully_saved');
                success(message);
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        rpc,
        success,
        failure,
        selected,
        previousLocationSelectedControl?.id,
        onBackPressed,
        name,
        bridgeDevice,
        inputType,
        direction,
        outputReverse,
        inputNumber,
        controlOption,
        sensorTypeOption,
    ]);

    const buttons = useMemo(() => [
        {
            text: t('delete'),
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selected?.id),
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            onClick: onGoBackPressed,
        },
    ], [
        t,
        onCreatePressed,
        onDeletePressed,
        selected,
        onGoBackPressed,
    ]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const setOutputReverseCheckbox = useCallback(() => {
        setOutputReverse(!outputReverse);
    }, [outputReverse]);

    useEffect(() => {
        setControlOption(controlOptionList.find(controlOptionItem => controlOptionItem.value === controlOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlOptionList]);

    useEffect(() => {
        setInputNumber(inputNumberOptionList.find(option => option.value === inputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputNumberOptionList]);

    useEffect(() => {
        if (previousLocationSelectedControl?.id != null) {
            setControlOption(controlOptionList.find(controlOption =>
                controlOption.value.id === previousLocationSelectedControl.id) || null);

            const nameSuffix = previousLocationSelectedControl.type_id === CONTROL_TYPE_ID_ALARM ?
                t('its_sensor') :
                t('its_key');
            setName(previousLocationSelectedControl.name + nameSuffix);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, previousLocationSelectedControl?.id]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_input_name')}
                            label={t('input_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('device')}
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        <SettingSelectComponent
                            label={t('input_type')}
                            options={inputTypeOptionList}
                            onChange={setInputType}
                            value={inputType}/>
                        {
                            renderIf(inputType?.value?.id === INPUT_TYPE_ID_SENSOR)(() =>
                                <SettingSelectComponent
                                    label={t('sensor_type')}
                                    options={sensorTypeOptionList}
                                    onChange={setSensorTypeOption}
                                    value={sensorTypeOption}/>)
                        }
                        {
                            renderIf(inputType?.value != null)(() =>
                                <>
                                    {
                                        renderIf(inputType.value.id !== INPUT_TYPE_ID_SCENARIO)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('control')}
                                                    options={controlOptionList}
                                                    onChange={setControlOption}
                                                    value={controlOption}/>
                                                {
                                                    renderIf(inputType.value.id !== INPUT_TYPE_ID_SENSOR)(() =>
                                                        <SettingSelectComponent
                                                            label={t('direction')}
                                                            options={DIRECTION_OPTION_LIST}
                                                            onChange={setDirection}
                                                            value={direction}/>,
                                                    )
                                                }
                                            </>,
                                        )
                                    }
                                    <SettingSelectComponent
                                        label={t('input_no')}
                                        options={inputNumberOptionList}
                                        onChange={setInputNumber}
                                        value={inputNumber}/>
                                </>,
                            )
                        }
                    </div>
                    <div className='content-wrapper'>
                        <SettingCheckBoxComponent
                            label={t('reverse')}
                            set={setOutputReverseCheckbox}
                            //todo , .
                            value={outputReverse}/>,
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
